<script setup lang="ts">
import { ref, onMounted, inject, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

// Check if we're in admin preview mode
const previewOrganizationId = inject<Ref<string> | null>('previewOrganizationId', null)

const embedUrl = ref<string | null>(null)
const isLoading = ref(true)
const iframeReady = ref(false)

onMounted(async () => {
  try {
    // Use admin endpoint for preview mode, regular endpoint otherwise
    const url = previewOrganizationId?.value
      ? `${BACKEND_URL}/api/admin/organizations/${previewOrganizationId.value}/homepage`
      : `${BACKEND_URL}/api/homepage`

    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    })

    // Redirect to disabled page if org is inactive
    if (response.status === 403) {
      const data = await response.json()
      if (data?.detail === 'Organization is inactive') {
        router.replace({ name: 'organization-disabled' })
        return
      }
      throw new Error(data?.detail || 'Unauthorized')
    }

    if (response.ok) {
      const data = await response.json()
      // Admin endpoint returns notion_page_id + notion_host, regular returns embed_url
      if (data?.notion_page_id && data?.notion_host) {
        embedUrl.value = `${data.notion_host}/ebd//${data.notion_page_id}`
      } else {
        embedUrl.value = data?.embed_url || null
      }
    }
  } catch (error) {
    console.error('Error fetching homepage:', error)
  } finally {
    isLoading.value = false
  }
})

function onIframeLoad() {
  iframeReady.value = true
}
</script>

<template>
  <AppComponent id="home" title="Home">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center h-full">
      <USkeleton class="h-[200px] w-[400px]" />
    </div>

    <!-- Notion embed -->
    <template v-else-if="embedUrl">
      <div :class="{ 'h-full': iframeReady, hidden: !iframeReady }">
        <iframe :src="embedUrl" frameborder="0" allowfullscreen @load="onIframeLoad"></iframe>
      </div>
      <div v-if="!iframeReady" class="flex justify-center items-center h-full">
        <USkeleton class="h-[200px] w-[400px]" />
      </div>
    </template>

    <!-- Default welcome message -->
    <UPageHero
      v-else
      title="Welcome to Ginjer AI"
      description="Access your insights from the sidebar."
    />
  </AppComponent>
</template>

<style scoped>
iframe {
  width: 100%;
  height: 100%;
  border: none;
  overflow: hidden;
  box-sizing: border-box;
}
</style>
